// ============================================================
// ByOurConsent.com -- Premium Membership Page
// /premium
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

// ============================================================
// Tool card component
// ============================================================
const ToolCard = ({ icon, suite, title, description, steps, link }) => {
    const [expanded, setExpanded] = useState(false);
    return (
        <div style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ padding: '1.25rem', cursor: 'pointer' }}
                onClick={() => setExpanded(!expanded)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{ fontSize: '28px', flexShrink: 0, lineHeight: 1 }}>{icon}</div>
                        <div>
                            <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '3px' }}>
                                {suite}
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>
                                {title}
                            </div>
                            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.5 }}>
                                {description}
                            </div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                        {link && (
                            <Link to={link} onClick={e => e.stopPropagation()}
                                style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '5px 12px', fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '2px', whiteSpace: 'nowrap' }}>
                                Use tool →
                            </Link>
                        )}
                        <div style={{ fontSize: '16px', color: '#8b6a3a' }}>
                            {expanded ? '−' : '+'}
                        </div>
                    </div>
                </div>
            </div>

            {expanded && (
                <div style={{ borderTop: '0.5px solid #c8beaa', padding: '1rem 1.25rem 1.25rem', background: '#ede8dc' }}>
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '8px' }}>
                        How it works
                    </div>
                    <ol style={{ margin: '0 0 0 1.1rem', padding: 0 }}>
                        {steps.map((step, i) => (
                            <li key={i} style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#4a5060', lineHeight: 1.6, marginBottom: '4px' }}>
                                {step}
                            </li>
                        ))}
                    </ol>
                    {link && (
                        <Link to={link}
                            style={{ display: 'inline-block', marginTop: '12px', background: '#8b1a1a', color: '#f5f0e8', padding: '8px 20px', fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '2px' }}>
                            Use this tool →
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};

// ============================================================
// Suite section
// ============================================================
const Suite = ({ title, subtitle, color, children }) => (
    <div style={{ marginBottom: '3rem' }}>
        <div style={{ borderLeft: `3px solid ${color}`, paddingLeft: '1rem', marginBottom: '1.25rem' }}>
            <div style={{ fontSize: '20px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                {title}
            </div>
            <div style={{ fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '2px' }}>
                {subtitle}
            </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {children}
        </div>
    </div>
);

// ============================================================
// Main PremiumPage
// ============================================================
// Pre-launch heads-up banner -- informational only, never blocks
// anything. Toggle off from Admin > Settings once ready for real launch.
const PrelaunchBanner = ({ settings }) => {
    if (settings.prelaunch_banner_enabled !== 'true') return null;
    return (
        <div style={{
            background: '#5a3a1a', color: '#f5f0e8', padding: '10px 20px',
            fontSize: '12px', fontFamily: 'sans-serif', textAlign: 'center', lineHeight: 1.5
        }}>
            {settings.prelaunch_banner_text ||
                "We're finishing up! Orders placed now are part of final testing."}
        </div>
    );
};

const PremiumPage = () => {
    const { user } = useAuth();
    const isAdmin = user && ['admin', 'superadmin'].includes(user.role);
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        api.get('/admin/settings/public')
            .then(res => setSettings(res.data.settings || {}))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (isAdmin) { setIsSubscribed(true); return; }
        if (user) {
            api.get('/subscriptions/status')
                .then(r => setIsSubscribed(r.data.isActive))
                .catch(() => {});
        }
    }, [user, isAdmin]);
    // Subscribed users see tools dashboard directly
    if (isSubscribed) {
        return (
            <>
                <Helmet><title>Premium Tools — By Our Consent</title></Helmet>
                <Nav />
                <PrelaunchBanner settings={settings} />
                <div style={{ background: '#1e2a3a', borderBottom: '2px solid #8b1a1a', padding: '1.5rem' }}>
                    <div style={{ maxWidth: '760px', margin: '0 auto' }}>
                        <div style={{ fontSize: '9px', fontFamily: 'sans-serif', letterSpacing: '0.16em', textTransform: 'uppercase', color: '#8b9a7a' }}>⭐ Premium member</div>
                        <h1 style={{ fontSize: '28px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginTop: '4px' }}>Your civic tools</h1>
                    </div>
                </div>
                <div style={{ maxWidth: '760px', margin: '0 auto', padding: '2rem 1.5rem' }}>
                    {[
                        { suite: 'Paperwork as a Weapon', tools: [
                            { to: '/premium/foia',          icon: '📋', label: 'FOIA & Public Records Generator',      desc: 'Generate FOIA request letters for any federal or state agency' },
                            { to: '/premium/notices',       icon: '📜', label: 'Constitutional Notice Generator',       desc: 'Formal notices to government officials citing constitutional violations' },
                            { to: '/premium/bill-analysis', icon: '🏛️', label: 'AI Bill Analysis',                     desc: 'Plain-English breakdown of any federal or state bill' },
                        ]},
                        { suite: 'Civic Intelligence', tools: [
                            { to: '/premium/constitutional-research', icon: '⚖️', label: 'Constitutional Research Assistant', desc: 'Research any constitutional question with case law and citations' },
                            { to: '/premium/rights-analyzer',         icon: '🛡️', label: 'Your Rights Analyzer',              desc: 'Know your rights in any specific situation' },
                            { to: '/premium/law-checker',             icon: '📰', label: 'Law vs Action Checker',             desc: 'Analyze any law, regulation, or executive order' },
                        ]},
                        { suite: 'Official Accountability', tools: [
                            { to: '/premium/official-accountability', icon: '🔎', label: 'Elected Official Accountability', desc: 'Who oversees them, how to file complaints, recall and removal' },
                            { to: '/premium/follow-the-money',        icon: '💰', label: 'Follow the Money',               desc: 'Financial records, campaign donors, and contracts roadmap' },
                            { to: '/premium/official-tracker',        icon: '📡', label: 'Official Tracker',               desc: 'Voting record, donor analysis, and accountability report card' },
                        ]},
                    ].map(({ suite, tools }) => (
                        <div key={suite} style={{ marginBottom: '2rem' }}>
                            <div style={{ fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '10px', fontWeight: 600 }}>{suite}</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {tools.map(({ to, icon, label, desc }) => (
                                    <Link key={to} to={to} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '14px 16px', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', textDecoration: 'none' }}
                                        onMouseEnter={e => e.currentTarget.style.background = '#ede8dc'}
                                        onMouseLeave={e => e.currentTarget.style.background = '#f5f0e8'}>
                                        <span style={{ fontSize: '22px', flexShrink: 0 }}>{icon}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>{label}</div>
                                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', marginTop: '2px' }}>{desc}</div>
                                        </div>
                                        <div style={{ background: '#8b1a1a', color: '#f5f0e8', padding: '5px 12px', fontSize: '10px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', borderRadius: '2px', flexShrink: 0 }}>
                                            Use tool →
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '0.5px solid #c8beaa', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a8070' }}>
                            Manage your membership on your <Link to="/account" style={{ color: '#8b1a1a' }}>account page</Link>.
                        </div>
                        <Link to="/account" style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#8a8070', textDecoration: 'none', border: '0.5px solid #c8beaa', padding: '5px 12px', borderRadius: '3px' }}>
                            Cancel membership →
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Helmet>
                <title>Premium Membership — By Our Consent</title>
                <meta name="description" content="Nine powerful civic tools for $9/month. FOIA generators, constitutional research, bill analysis, rights analyzer and more." />
            </Helmet>

            <Nav />
            <PrelaunchBanner settings={settings} />

            {/* Hero */}
            <div style={{ background: 'linear-gradient(135deg, #1e2a3a 0%, #2a3a4e 100%)', borderBottom: '2px solid #8b1a1a', padding: '3rem 1.5rem', textAlign: 'center' }}>
                <div style={{ maxWidth: '680px', margin: '0 auto' }}>
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#8b9a7a', marginBottom: '12px' }}>
                        ⭐ Premium membership
                    </div>
                    <h1 style={{ fontSize: '40px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1.1, marginBottom: '16px' }}>
                        Nine tools that turn citizens into a force to be reckoned with
                    </h1>
                    <p style={{ fontSize: '16px', fontFamily: 'sans-serif', color: '#8a9aaa', lineHeight: 1.7, marginBottom: '2rem' }}>
                        The same research, paperwork, and analysis that takes lawyers hundreds of dollars
                        and citizens days of frustration — done in minutes. Constitutional research,
                        FOIA requests, bill analysis, rights guidance, and official accountability tools.
                        All in one place. All for $9 a month.
                    </p>

                    {/* Price */}
                    <div style={{ display: 'inline-block', background: 'rgba(139,26,26,0.2)', border: '0.5px solid #8b1a1a', borderRadius: '4px', padding: '1.25rem 2.5rem', marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '36px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", lineHeight: 1 }}>
                            $9 <span style={{ fontSize: '16px', fontWeight: 400, color: '#8a9aaa' }}>/ month</span>
                        </div>
                        <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#8a9aaa', marginTop: '6px' }}>
                            30-day free trial · Cancel anytime · No contracts
                        </div>
                    </div>

                    <br />

                    {/* CTA */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <Link to="/premium/subscribe" style={{ display: 'inline-block', background: '#8b1a1a', color: '#f5f0e8', padding: '12px 32px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', borderRadius: '3px', textDecoration: 'none' }}>
                            Start free trial →
                        </Link>
                        {!user && (
                            <Link to="/register" style={{ background: 'transparent', color: '#e8dfc8', border: '0.5px solid #3a4e64', padding: '12px 24px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.06em', textTransform: 'uppercase', textDecoration: 'none', borderRadius: '3px' }}>
                                Create free account first
                            </Link>
                        )}
                    </div>

                </div>
            </div>

            {/* What makes this different */}
            <div style={{ background: '#ede8dc', borderBottom: '0.5px solid #c8beaa', padding: '1.5rem' }}>
                <div style={{ maxWidth: '860px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
                    {[
                        { icon: '⚡', title: 'Minutes, not days', desc: 'What used to take hours of legal research or bureaucratic navigation now takes minutes with guided wizards.' },
                        { icon: '📄', title: 'Real documents', desc: 'Generate legally formatted FOIA requests, notices, and analyses you can actually use — not just information.' },
                        { icon: '🎯', title: 'State-specific', desc: 'Every tool knows the difference between federal and state law, and adapts to your specific state and situation.' }
                    ].map(({ icon, title, desc }) => (
                        <div key={title} style={{ textAlign: 'center', padding: '1rem' }}>
                            <div style={{ fontSize: '28px', marginBottom: '8px' }}>{icon}</div>
                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '4px' }}>{title}</div>
                            <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.5 }}>{desc}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tools */}
            <div style={{ maxWidth: '860px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>

                {/* Mission statement */}
                <div style={{ background: '#1e2a3a', borderRadius: '4px', padding: '2rem', marginBottom: '3rem', borderLeft: '3px solid #8b1a1a' }}>
                    <div style={{ fontSize: '22px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem' }}>
                        Why this exists
                    </div>
                    <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#8a9aaa', lineHeight: 1.8 }}>
                        The average citizen who suspects government corruption, wants to challenge an unconstitutional
                        law, or simply wants to know their rights faces the same obstacle: navigating a legal and
                        bureaucratic system that was designed by professionals, for professionals.
                        <br /><br />
                        A FOIA request drafted incorrectly gets rejected on a technicality. A constitutional question
                        researched on Google returns conflicting opinions. A bill that strips your rights passes
                        because nobody had time to read 400 pages of legalese. An elected official faces zero
                        accountability because most people don't know where to start.
                        <br /><br />
                        <span style={{ color: '#e8dfc8', fontWeight: 600 }}>Premium membership changes that.</span>
                        {' '}Nine guided tools that turn the paperwork, research, and analysis that costs
                        hundreds of dollars and days of effort into something any citizen can do in minutes.
                        No law degree required.
                    </div>
                </div>

                {/* Suite overview cards */}
                <div style={{ marginBottom: '3rem' }}>
                    <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                        <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '8px' }}>What you get</div>
                        <h2 style={{ fontSize: '26px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif" }}>
                            Three suites. Nine tools. One mission.
                        </h2>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
                        {[
                            {
                                color: '#8b1a1a', icon: '📋',
                                title: 'Paperwork as a Weapon',
                                desc: 'The bureaucratic system is used against citizens every day. These tools flip that — putting the same legal paperwork power in your hands.',
                                tools: ['FOIA & Public Records Generator', 'Constitutional Notice Generator', 'AI Bill Analysis']
                            },
                            {
                                color: '#2a4a8a', icon: '⚖️',
                                title: 'Civic Intelligence Suite',
                                desc: 'Guided AI research that answers your constitutional and legal questions precisely. Structured intake. Cited answers. No vague generalities.',
                                tools: ['Constitutional Research Assistant', 'Your Rights Analyzer', 'Law vs Action Checker']
                            },
                            {
                                color: '#3b6d11', icon: '🔎',
                                title: 'Official Accountability Suite',
                                desc: 'Research, track, and hold officials accountable with tools that show you exactly who to contact, what records exist, and how to act.',
                                tools: ['Elected Official Accountability', 'Follow the Money', 'Official Tracker']
                            }
                        ].map(({ color, icon, title, desc, tools }) => (
                            <div key={title} style={{ background: '#f5f0e8', border: '0.5px solid #c8beaa', borderTop: `3px solid ${color}`, borderRadius: '4px', padding: '1.25rem' }}>
                                <div style={{ fontSize: '24px', marginBottom: '8px' }}>{icon}</div>
                                <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '6px' }}>{title}</div>
                                <div style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#6a7080', lineHeight: 1.6, marginBottom: '12px' }}>{desc}</div>
                                <div style={{ borderTop: '0.5px solid #c8beaa', paddingTop: '10px' }}>
                                    {tools.map(t => (
                                        <div key={t} style={{ fontSize: '12px', fontFamily: 'sans-serif', color: '#1e2a3a', padding: '3px 0', display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <span style={{ color, fontSize: '10px' }}>▸</span> {t}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Value comparison */}
                    <div style={{ background: '#ede8dc', border: '0.5px solid #c8beaa', borderRadius: '4px', padding: '1.5rem' }}>
                        <div style={{ fontSize: '15px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '1rem' }}>
                            What this would cost without By Our Consent
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                            {[
                                { item: 'FOIA request drafted by attorney', cost: '$300–$500', each: 'per request' },
                                { item: 'Constitutional research session', cost: '$400–$800', each: 'per hour' },
                                { item: 'Bill analysis by legal staff', cost: '$500–$1,500', each: 'per bill' },
                                { item: 'Rights consultation', cost: '$200–$400', each: 'per consultation' },
                                { item: 'Official accountability research', cost: 'Days of work', each: 'per official' },
                                { item: 'All 9 tools, unlimited use', cost: '$9', each: 'per month' }
                            ].map(({ item, cost, each }, i) => (
                                <div key={item} style={{ padding: '10px 12px', background: i === 5 ? '#1e2a3a' : '#f5f0e8', border: `0.5px solid ${i === 5 ? '#8b1a1a' : '#c8beaa'}`, borderRadius: '3px' }}>
                                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: i === 5 ? '#8a9aaa' : '#6a7080', marginBottom: '4px', lineHeight: 1.4 }}>{item}</div>
                                    <div style={{ fontSize: '20px', fontWeight: 600, color: i === 5 ? '#e8dfc8' : '#8b1a1a', fontFamily: "'EB Garamond', Georgia, serif" }}>{cost}</div>
                                    <div style={{ fontSize: '10px', fontFamily: 'sans-serif', color: i === 5 ? '#8b9a7a' : '#8a8070', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{each}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#8b6a3a', marginBottom: '8px' }}>
                        The tools
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: 600, color: '#1e2a3a', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '8px' }}>
                        Nine tools across three suites
                    </h2>
                    <p style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#6a7080', maxWidth: '500px', margin: '0 auto' }}>
                        Click any tool to see exactly how it works and what it produces.
                    </p>
                </div>

                {/* Suite 1 -- Paperwork as a Weapon */}
                <Suite
                    title="Paperwork as a Weapon"
                    subtitle="Turn bureaucracy against overreach. Generate legally formatted documents in minutes."
                    color="#8b1a1a">

                    <ToolCard
                        icon="📋"
                        suite="Paperwork as a weapon"
                        title="FOIA & Public Records Generator"
                        description="Generate a legally bulletproof Freedom of Information Act request tailored to your state and agency — in minutes."
                        link="/premium/foia"
                        steps={[
                            'Select federal or state, then choose your state',
                            'Select the government agency type (police dept, school board, city council, federal agency, etc.)',
                            'Check off what you\'re investigating (emails, contracts, budgets, meeting minutes, personnel records, etc.)',
                            'Describe in your own words what you\'re looking for',
                            'App generates a properly formatted request with correct state-specific legal citations, fee waiver language, and the deadline they must respond by',
                            'Includes what to do if denied and pre-written appeal language',
                            'Download as a Word document — edit and send'
                        ]}
                    />

                    <ToolCard
                        icon="📜"
                        suite="Paperwork as a weapon"
                        title="Constitutional Notice Generator"
                        description="Formally put government officials on notice when they overstep. Professionally formatted notices that create a legal paper trail."
                        link="/premium/notices"
                        steps={[
                            'Select your situation: school board, code enforcement, local official, employer mandate, HOA, or other',
                            'Select notice type: Notice of Non-Consent, Notice of Constitutional Violation, Administrative Grievance, Cease and Desist, or Formal Public Comment',
                            'Answer guided questions about the specific conduct and who is responsible',
                            'Select your state',
                            'App generates a professionally formatted notice citing the correct constitutional provisions and legal standards',
                            'Download as a Word document — review, add your name and signature line, send'
                        ]}
                    />

                    <ToolCard
                        icon="🏛️"
                        suite="Paperwork as a weapon"
                        title="AI Bill Analysis"
                        description="Enter any federal or state bill number and get a plain-English breakdown — including constitutional red flags, hidden pork, and ready-made opposition language."
                        link="/premium/bill-analysis"
                        steps={[
                            'Enter a federal bill number (e.g. HR 1234) or a state bill number with your state',
                            'Or paste the full bill text directly',
                            'App retrieves the bill and analyzes it with AI',
                            'Returns: plain English summary of what it actually does, constitutional concerns with supporting precedents, pork and unrelated riders identified, fiscal impact in plain language',
                            'Generates ready-made language you can use in public comments, letters to representatives, or social media',
                            'Shows sponsor, co-sponsors, and their relevant voting history',
                            'Download as a shareable Word report'
                        ]}
                    />
                </Suite>

                {/* Suite 2 -- Civic Intelligence */}
                <Suite
                    title="Civic Intelligence Suite"
                    subtitle="Guided AI research tools that answer your constitutional and legal questions precisely."
                    color="#2a4a8a">

                    <ToolCard
                        icon="⚖️"
                        suite="Civic intelligence"
                        title="Constitutional Research Assistant"
                        description="Research any constitutional question about federal or state constitutions. Guided intake ensures you get a precise, well-cited answer."
                        link="/premium/constitutional-research"
                        steps={[
                            'Select: Federal Constitution, State Constitution, or Compare both',
                            'Select your question type: Does this right exist? What does this mean? Does this action violate the constitution? What process is required? How does federal vs state authority work?',
                            'Answer a few focused questions to build the exact research query',
                            'Review the formulated question before submitting — adjust if needed',
                            'Returns: constitutional basis (article/amendment/section), plain language explanation, relevant case law, current interpretation, and federal vs state distinctions',
                            'Save your research for future reference'
                        ]}
                    />

                    <ToolCard
                        icon="🛡️"
                        suite="Civic intelligence"
                        title="Your Rights Analyzer"
                        description="Know exactly what your rights are in any specific situation — and what authorities can and cannot legally do."
                        link="/premium/rights-analyzer"
                        steps={[
                            'Select your situation: traffic stop, search or seizure, arrest, employment, voting, public assembly, free speech, or other',
                            'Answer a few questions about the specific circumstances',
                            'Select your state (some rights vary by state)',
                            'Returns: your specific constitutional and legal rights in this situation, what authorities are legally permitted to do, what they are NOT permitted to do, what you should and should not say, how to document the encounter, who to contact if your rights are violated',
                            'Includes relevant court decisions that established these rights'
                        ]}
                    />

                    <ToolCard
                        icon="📰"
                        suite="Civic intelligence"
                        title="Law vs Action Checker"
                        description="Paste any law, regulation, or executive order and get a plain-language analysis of what it actually says and whether it exceeds legal authority."
                        link="/premium/law-checker"
                        steps={[
                            'Paste the text of a law, regulation, executive order, or local ordinance',
                            'Select the level: federal, state, or local',
                            'Select what you want to know: plain language translation, does it exceed authority, is it constitutional, or all three',
                            'Returns: plain English translation of what it actually does, analysis of whether the issuing body has the authority to do this, constitutional questions raised, similar laws that have been challenged or struck down',
                            'Flags areas of genuine legal dispute vs settled law'
                        ]}
                    />
                </Suite>

                {/* Suite 3 -- Official Accountability */}
                <Suite
                    title="Official Accountability Suite"
                    subtitle="Research, track, and hold elected officials and government agencies accountable."
                    color="#3b6d11">

                    <ToolCard
                        icon="🔎"
                        suite="Official accountability"
                        title="Elected Official Accountability"
                        description="Find out exactly who an official is accountable to, what oversight exists, and what steps you can take when they fail to do their job."
                        link="/premium/official-accountability"
                        steps={[
                            'Enter the official\'s name and position (senator, city council member, school board member, etc.)',
                            'Describe the action or failure you are concerned about',
                            'Returns: who they are directly accountable to, relevant ethics laws and standards that apply to their position, oversight bodies with contact information, how to file a formal complaint, recall or removal process if applicable, relevant precedents of similar accountability actions'
                        ]}
                    />

                    <ToolCard
                        icon="💰"
                        suite="Official accountability"
                        title="Follow the Money"
                        description="Know where to look when you suspect financial conflicts of interest. A guided roadmap to public financial records, campaign finance, and contracts."
                        link="/premium/follow-the-money"
                        steps={[
                            'Enter the name of an official, agency, or contractor',
                            'Select what you are investigating: campaign donors, government contracts, personal financial disclosures, or all three',
                            'Returns: direct links to relevant public databases (FEC, USASpending, state disclosure sites), what to look for and how to read the records, red flags that indicate potential conflicts, how to file a FOIA request for additional financial records, who to report findings to'
                        ]}
                    />

                    <ToolCard
                        icon="📡"
                        suite="Official accountability"
                        title="Official Tracker"
                        description="Follow specific elected officials and get notified when their voting record, public statements, or financial disclosures change."
                        link="/premium/official-tracker"
                        steps={[
                            'Search for any federal or state elected official by name',
                            'View their voting record on key constitutional and civic issues',
                            'See campaign donor summary and financial disclosure highlights',
                            'Follow the official — receive alerts when they cast notable votes, make public statements, or update financial disclosures',
                            'Compare their stated positions to their actual voting record',
                            'Generate a shareable accountability report'
                        ]}
                    />
                </Suite>

                {/* Bottom CTA */}
                <div style={{ background: '#1e2a3a', borderRadius: '4px', padding: '2rem', textAlign: 'center', borderLeft: '3px solid #8b1a1a' }}>
                    <div style={{ fontSize: '24px', fontWeight: 600, color: '#e8dfc8', fontFamily: "'EB Garamond', Georgia, serif", marginBottom: '8px' }}>
                        Nine tools. $9 a month.
                    </div>
                    <div style={{ fontSize: '14px', fontFamily: 'sans-serif', color: '#8a9aaa', marginBottom: '1.5rem', lineHeight: 1.6 }}>
                        A single FOIA request drafted by a lawyer costs more than a year of this membership.
                        A constitutional research session with an attorney costs more than five years.
                        These tools put that power in your hands every day.
                    </div>
                    <Link to="/premium/subscribe" style={{ display: 'inline-block', background: '#8b1a1a', color: '#f5f0e8', padding: '12px 32px', fontSize: '13px', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', borderRadius: '3px', textDecoration: 'none', marginBottom: '8px' }}>
                        Start free trial →
                    </Link>
                    <div style={{ fontSize: '11px', fontFamily: 'sans-serif', color: '#5a6a7a' }}>
                        30-day free trial · Cancel anytime · No party affiliation
                    </div>
                </div>

                {/* Free tier reminder */}
                <div style={{ marginTop: '1.5rem', padding: '1rem 1.5rem', background: '#f5f0e8', border: '0.5px solid #c8beaa', borderRadius: '4px', textAlign: 'center', fontSize: '13px', fontFamily: 'sans-serif', color: '#6a7080' }}>
                    Free membership still gets you articles, forum, chapters, and civic actions.{' '}
                    <Link to="/register" style={{ color: '#8b1a1a' }}>Create a free account →</Link>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default PremiumPage;
